import type {
  AiProductDraft,
  BoxListItem,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import { Measures } from "../../../types/entities";
import { formatBoxReferenceInputValue } from "../../../utils/boxReference";
import { applyAiProductDraft } from "./ai-autofill";
import {
  buildCreateProductPayload,
  type AddProductFormState,
  validateAddProductForm,
} from "./form-state";
import { loadAddProductReferenceData, submitAddProduct } from "./submit-flow";

export interface ClearedAddProductUiState {
  name: string;
  description: string;
  photo: string;
  photos: string[];
  typeTags: string[];
  typeTagInput: string;
  parentId: string;
  manufacturingDate: string;
  expiryDate: string;
  measure: Measures;
  quantityInput: string;
  error: string | null;
  isPhotoLoading: boolean;
  isVoicePanelOpen: boolean;
  voiceTranscript: string;
  voiceError: string | null;
  voiceInfo: string | null;
}

export function resolveActionErrorMessage(
  error: unknown,
  fallback: string,
): string {
  return error instanceof Error ? error.message : fallback;
}

export async function loadAddProductReferenceState(input: {
  api: IWarehouseApi;
  parentId: string;
}): Promise<{
  availableBoxes: BoxListItem[];
  typeTagSuggestions: string[];
  parentId: string;
}> {
  const references = await loadAddProductReferenceData(input.api);

  return {
    availableBoxes: references.boxes,
    typeTagSuggestions: references.typeTagSuggestions,
    parentId: formatBoxReferenceInputValue(input.parentId, references.boxes),
  };
}

export async function applyAiAutofillDraft(input: {
  api: IWarehouseApi;
  transcript?: string;
  photo?: string | null;
  state: AddProductFormState;
  availableBoxes: BoxListItem[];
  formatParentInputValue: (value: string) => string;
}): Promise<string> {
  const transcript =
    typeof input.transcript === "string" ? input.transcript.trim() : "";
  const photo = typeof input.photo === "string" ? input.photo.trim() : "";
  const result = await input.api.fillFormDraft({
    entity: "product",
    transcript: transcript || undefined,
    photo: photo || undefined,
  });
  if (result.entity !== "product") {
    throw new Error("Некорректный тип AI-ответа для формы продукта");
  }

  applyAiProductDraftToState({
    draft: result.draft as AiProductDraft,
    state: input.state,
    availableBoxes: input.availableBoxes,
    formatParentInputValue: input.formatParentInputValue,
  });

  return result.model;
}

export function applyAiProductDraftToState(input: {
  draft: AiProductDraft;
  state: AddProductFormState;
  availableBoxes: BoxListItem[];
  formatParentInputValue: (value: string) => string;
}): void {
  applyAiProductDraft({
    draft: input.draft,
    state: input.state,
    availableBoxes: input.availableBoxes,
    formatParentInputValue: input.formatParentInputValue,
  });
}

export function validateAddProductSubmit(input: {
  state: AddProductFormState;
  availableBoxes: BoxListItem[];
}): string | null {
  return validateAddProductForm(input.state, input.availableBoxes);
}

export async function submitAddProductForm(input: {
  api: IWarehouseApi;
  state: AddProductFormState;
  availableBoxes: BoxListItem[];
}): Promise<Awaited<ReturnType<typeof submitAddProduct>>> {
  const payload = buildCreateProductPayload(input.state, input.availableBoxes);
  return submitAddProduct(input.api, payload);
}

export function createClearedAddProductUiState(input: {
  presetParentId: string;
  availableBoxes: BoxListItem[];
}): ClearedAddProductUiState {
  return {
    name: "",
    description: "",
    photo: "",
    photos: [],
    typeTags: [],
    typeTagInput: "",
    parentId: formatBoxReferenceInputValue(
      input.presetParentId,
      input.availableBoxes,
    ),
    manufacturingDate: "",
    expiryDate: "",
    measure: Measures.quantity,
    quantityInput: "1",
    error: null,
    isPhotoLoading: false,
    isVoicePanelOpen: false,
    voiceTranscript: "",
    voiceError: null,
    voiceInfo: null,
  };
}
